import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { platformConfig } from '../../components/PlatformIcons';
import {
  updateAutomod,
  updateBio,
  updateDmInstructions,
  updateProfileDescription,
  updateSocial,
} from '../../api/social';
import { testModComment } from '../../api/comment';
import { useSocial } from '../../context/socialProvider';
import CategorySlider from '../../components/CategorySlider';
import React from 'react';
import { CategoryScores } from '../../types/categoryScores';
import SliderDescriptions from './SliderDescriptions';

const SocialSettings = () => {
  const { providerId, providerName: routeProviderName } = useParams<{
    providerId: string;
    providerName: string;
  }>();
  const resultRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // State management for form fields
  const [initialBio, setInitialBio] = useState('');
  const [bio, setBio] = useState('');

  const [initialDmInstructions, setInitialDmInstructions] = useState('');
  const [dmInstructions, setDmInstructions] = useState('');
  const [initialProfileDescription, setInitialProfileDescription] =
    useState('');
  const [profileDescription, setProfileDescription] = useState('');

  const [autoModActive, setAutoModActive] = useState(false);
  const [initialAutoModActive, setInitialAutoModActive] = useState(false);
  const [modComment, setModComment] = useState('');
  const [isTestingMod, setIsTestingMod] = useState(false);
  const [modCommentResult, setModCommentResult] = useState<{
    evaluation: boolean;
    reasoning: string;
  } | null>(null);

  const [autoModInstructions, setAutoModInstructions] = useState('');
  const [initialAutoModInstructions, setInitialAutoModInstructions] =
    useState('');
  const [initialCategoryScores, setInitialCategoryScores] =
    useState<CategoryScores>({
      sexual: 1,
      harassment: 1,
      hate: 1,
      illicit: 1,
      'self-harm': 1,
      violence: 1,
    });

  // Loading states for save operations
  const [isSavingProfileDescription, setIsSavingProfileDescription] =
    useState(false);
  const [isSavingBio, setIsSavingBio] = useState(false); // For Comment Instructions
  const [isSavingDmInstructions, setIsSavingDmInstructions] = useState(false);
  const [isSavingAutomod, setIsSavingAutomod] = useState(false);

  const { availableSocials } = useSocial();
  const { currentSocial, refreshSocialInfo } = useSocial();

  // Get the selected social account based on providerName param
  const [selectedSocial, setSelectedSocial] = useState<any | null>(null);

  const [autoGenerateCommentsEnabled, setAutoGenerateCommentsEnabled] =
    useState(false);
  const [
    initialAutoGenerateCommentsEnabled,
    setInitialAutoGenerateCommentsEnabled,
  ] = useState(false);
  const [isSavingAutoGenerate, setIsSavingAutoGenerate] = useState(false);

  const getSortedCategories = (
    categoryScores: CategoryScores
  ): [string, number][] => {
    return Object.entries(categoryScores).sort(([keyA], [keyB]) =>
      keyA.localeCompare(keyB)
    );
  };

  // Add state for tracking which section is expanded
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  // Helper to get a preview sentence from a string
  const getPreview = (text: string): React.ReactNode => {
    if (!text)
      return <span className="italic text-gray-400">No content set</span>;
    const trimmed = text.trim().replace(/\n+/g, ' '); // Replace newlines with a space
    if (trimmed.length <= 60) return trimmed; // Returns a plain string
    return trimmed.slice(0, 60) + '...'; // Returns a plain string
  };

  // Ensure we have a valid providerName from the route
  useEffect(() => {
    if (!routeProviderName) {
      navigate('/global/settings');
    }
  }, [routeProviderName, navigate]);

  useEffect(() => {
    if (providerId && routeProviderName && availableSocials.length > 0) {
      const decodedProviderName = decodeURIComponent(routeProviderName);

      const social = availableSocials.find(
        (s) =>
          s.id.toString() === providerId &&
          (s.providerName === decodedProviderName ||
            encodeURIComponent(s.providerName) === routeProviderName)
      );

      if (social) {
        setSelectedSocial(social);

        const autoGenerateEnabled =
          social?.autoGenerateCommentsEnabled ?? false;
        setAutoGenerateCommentsEnabled(autoGenerateEnabled);
        setInitialAutoGenerateCommentsEnabled(autoGenerateEnabled);

        // Set initial values from the selected social
        setProfileDescription(social.profileDescription || '');
        setInitialProfileDescription(social.profileDescription || '');
        setBio(social.bio || '');
        setInitialBio(social.bio || '');
        setDmInstructions(social.dmDescription || '');
        setInitialDmInstructions(social.dmDescription || '');
        setAutoModInstructions(social.autoModInstructions || '');
        setInitialAutoModInstructions(social.autoModInstructions || '');

        if (social.categoryScores) {
          setValues(social.categoryScores);
          setInitialCategoryScores(social.categoryScores);
        } else {
          // Ensure initialCategoryScores is reset if not present
          const defaultScores: CategoryScores = {
            sexual: 1,
            harassment: 1,
            hate: 1,
            illicit: 1,
            'self-harm': 1,
            violence: 1,
          };
          setValues(defaultScores);
          setInitialCategoryScores(defaultScores);
        }

        if (social.autoModActive !== undefined) {
          setAutoModActive(social.autoModActive);
          setInitialAutoModActive(social.autoModActive);
        } else {
          setAutoModActive(false);
          setInitialAutoModActive(false);
        }
      } else {
        navigate('/global/settings');
      }
    } else if (!routeProviderName && availableSocials.length > 0) {
      navigate('/global/settings');
    }
  }, [providerId, routeProviderName, availableSocials, navigate]);

  const handleAutomodSave = async () => {
    if (!selectedSocial) return;
    setIsSavingAutomod(true);
    try {
      await updateAutomod(
        selectedSocial.id,
        autoModActive,
        autoModInstructions,
        values
      );
      setInitialAutoModActive(autoModActive);
      setInitialAutoModInstructions(autoModInstructions);
      setInitialCategoryScores({ ...values });
      // Optionally: show success notification
    } catch (error) {
      console.error('Error saving automod settings:', error);
      // Optionally: show error notification
    } finally {
      if (currentSocial?.id === selectedSocial.id) {
        await refreshSocialInfo();
      }
      setIsSavingAutomod(false);
    }
  };

  const handleToggleAutoMod = () => {
    setAutoModActive(!autoModActive);
  };

  const handleTestModComment = async () => {
    if (selectedSocial && modComment && !isTestingMod) {
      setIsTestingMod(true);
      try {
        const result = await testModComment(modComment, selectedSocial.id);
        setModCommentResult(result.data);
        setTimeout(() => {
          resultRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 100);
      } catch (error) {
        console.error('Error testing mod comment:', error);
        // Optionally: show error notification
      } finally {
        setIsTestingMod(false);
      }
    }
  };

  const handleSave = async (
    setInitialValue: (value: string) => void,
    value: string,
    field: 'profileDescription' | 'bio' | 'dmInstructions'
  ) => {
    if (!selectedSocial) return;

    let setLoadingState: React.Dispatch<React.SetStateAction<boolean>>;

    switch (field) {
      case 'profileDescription':
        setLoadingState = setIsSavingProfileDescription;
        break;
      case 'bio': // Corresponds to Comment Instructions
        setLoadingState = setIsSavingBio;
        break;
      case 'dmInstructions':
        setLoadingState = setIsSavingDmInstructions;
        break;
      default:
        console.error('Unknown field in handleSave:', field);
        return;
    }

    setLoadingState(true);
    try {
      switch (field) {
        case 'bio':
          await updateBio(selectedSocial.id, value);
          break;
        case 'dmInstructions':
          await updateDmInstructions(selectedSocial.id, value);
          break;
        case 'profileDescription':
          await updateProfileDescription(selectedSocial.id, value);
          break;
      }
      if (currentSocial?.id === selectedSocial.id) {
        await refreshSocialInfo();
      }
      setInitialValue(value);
      // Optionally: show success notification
    } catch (error) {
      console.error(`Error saving ${field}:`, error);
      // Optionally: show error notification
    } finally {
      setLoadingState(false);
    }
  };

  const [values, setValues] = React.useState<CategoryScores>({
    sexual: 1,
    harassment: 1,
    hate: 1,
    illicit: 1,
    'self-harm': 1,
    violence: 1,
  });

  const handleChange = (category: string, newValue: number): void => {
    setValues((prev) => ({
      ...prev,
      [category]: newValue,
    }));
  };

  if (!selectedSocial) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <div className="animate-pulse text-white">
          Loading social account settings...
        </div>
      </div>
    );
  }

  const handleToggleAutoGenerateComments = () => {
    const newValue = !autoGenerateCommentsEnabled;
    setAutoGenerateCommentsEnabled(newValue);
  };

  const handleAutoGenerateSave = async () => {
    if (!currentSocial) return;

    setIsSavingAutoGenerate(true);
    try {
      // Call your API endpoint
      await updateSocial(
        currentSocial.id,
        'autoGenerateCommentsEnabled',
        autoGenerateCommentsEnabled
      );

      // Update the initial value so the save button disappears
      setInitialAutoGenerateCommentsEnabled(autoGenerateCommentsEnabled);

      // Optional: Show success message
      console.log('Auto-generate comments setting saved successfully');
    } catch (error) {
      console.error('Failed to save auto-generate comments setting:', error);
      // Optional: Show error message to user
      // You might want to revert the toggle state here
      setAutoGenerateCommentsEnabled(initialAutoGenerateCommentsEnabled);
    } finally {
      setIsSavingAutoGenerate(false);
      if (currentSocial?.id === selectedSocial.id) {
        await refreshSocialInfo();
      }
    }
  };

  return (
    <div className="h-screen bg-black">
      <div className="flex flex-col space-y-4 pb-6 mt-4">
        {/* Profile Summary Section */}
        <SettingsSection
          id="profile"
          title="Profile Summary"
          description="A summary of your profile."
          isExpanded={expandedSection === 'profile'}
          onToggle={() =>
            setExpandedSection(expandedSection === 'profile' ? null : 'profile')
          }
          preview={getPreview(profileDescription)}
        >
          <textarea
            className="bg-black border border-gray-600 rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet h-80"
            placeholder="Enter your summary here..."
            value={profileDescription}
            onChange={(e) => setProfileDescription(e.target.value)}
          />
          <Save
            initialValue={initialProfileDescription}
            currentValue={profileDescription}
            isLoading={isSavingProfileDescription}
            onClick={() =>
              handleSave(
                setInitialProfileDescription,
                profileDescription,
                'profileDescription'
              )
            }
          />
        </SettingsSection>

        {/* Comment Instructions Section */}
        <SettingsSection
          id="comments"
          title="Comment Instructions"
          description="Provide specific instructions for how the AI should handle comments. For example, 'Be more concise in comment replies' or 'Don't use emojis'."
          isExpanded={expandedSection === 'comments'}
          onToggle={() =>
            setExpandedSection(
              expandedSection === 'comments' ? null : 'comments'
            )
          }
          preview={getPreview(bio)}
        >
          <textarea
            className="bg-black border border-gray-600 rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet h-80"
            placeholder="Enter your comment instructions here..."
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />
          <Save
            initialValue={initialBio}
            currentValue={bio}
            isLoading={isSavingBio}
            onClick={() => handleSave(setInitialBio, bio, 'bio')}
          />
        </SettingsSection>

        {/* DM Instructions Section */}
        <SettingsSection
          id="dm"
          title="DM Instructions"
          description="Provide specific instructions for how the AI should handle direct messages. For example, 'Be concise in DM responses' or 'Always ask for clarification if the question is vague'."
          isExpanded={expandedSection === 'dm'}
          onToggle={() =>
            setExpandedSection(expandedSection === 'dm' ? null : 'dm')
          }
          preview={getPreview(dmInstructions)}
        >
          <textarea
            className="bg-black border border-gray-600 rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet h-80"
            placeholder="Enter your DM instructions here..."
            value={dmInstructions}
            onChange={(e) => setDmInstructions(e.target.value)}
          />
          <Save
            initialValue={initialDmInstructions}
            currentValue={dmInstructions}
            isLoading={isSavingDmInstructions}
            onClick={() =>
              handleSave(
                setInitialDmInstructions,
                dmInstructions,
                'dmInstructions'
              )
            }
          />
        </SettingsSection>

        <div className="settings-container">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-xl">
                Auto-Generate Comment Responses
              </h2>
              <p className="text-sm text-gray-300 mt-1">
                Automatically generate AI-powered responses to comments on your
                posts. <br />
                <span className="text-xs">
                  Turning this on may slow down comment loading. Turn it off for
                  faster loading.
                </span>
              </p>
            </div>
            <button
              onClick={handleToggleAutoGenerateComments}
              className={`relative inline-flex items-center h-7 w-14 rounded-full transition-colors duration-200 focus:outline-none ${
                autoGenerateCommentsEnabled ? 'bg-violet' : 'bg-gray-400'
              }`}
              aria-pressed={autoGenerateCommentsEnabled}
              aria-label="Toggle Auto-Generate Comments"
            >
              <span
                className={`inline-block w-6 h-6 transform bg-white rounded-full shadow transition-transform duration-200 ${
                  autoGenerateCommentsEnabled
                    ? 'translate-x-7'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex justify-end mt-4">
            <Save
              initialValue={initialAutoGenerateCommentsEnabled}
              currentValue={autoGenerateCommentsEnabled}
              isLoading={isSavingAutoGenerate}
              onClick={handleAutoGenerateSave}
            />
          </div>
        </div>

        {/* --- Redesigned AutoMod Module --- */}
        <div className="settings-container bio bg-grey rounded-xl p-6 mb-8 shadow-lg border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-xl text-white flex items-center">
                AutoMod
                <span
                  className={`ml-3 px-2 py-1 rounded text-xs font-bold ${
                    autoModActive
                      ? 'bg-green-500 text-black'
                      : 'bg-gray-400 text-white'
                  }`}
                >
                  {autoModActive ? 'Active' : 'Inactive'}
                </span>
              </h2>
              <p className="text-sm text-gray-300 mt-1 max-w-xl">
                Configure automatic moderation for this platform. Set
                instructions and adjust category thresholds. Test moderation
                below.
              </p>
            </div>
            <button
              onClick={handleToggleAutoMod}
              className={`relative inline-flex items-center h-7 w-14 rounded-full transition-colors duration-200 focus:outline-none ${
                autoModActive ? 'bg-violet' : 'bg-gray-400'
              }`}
              aria-pressed={autoModActive}
              aria-label="Toggle AutoMod"
            >
              <span
                className={`inline-block w-6 h-6 transform bg-white rounded-full shadow transition-transform duration-200 ${
                  autoModActive ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Moderation Instructions
              </label>
              <textarea
                className="bg-black border border-gray-600 rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet"
                style={{ height: '533px' }}
                placeholder="Describe what should be automatically hidden..."
                value={autoModInstructions}
                onChange={(e) => setAutoModInstructions(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Category Thresholds
              </label>
              <div className="space-y-3">
                {getSortedCategories(values).map(([key, value]) => (
                  <CategorySlider
                    key={key}
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    value={value}
                    onChange={(newValue) => handleChange(key, newValue)}
                    descriptions={SliderDescriptions}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Save
              initialValue={{
                active: initialAutoModActive,
                instructions: initialAutoModInstructions,
                scores: initialCategoryScores,
              }}
              currentValue={{
                active: autoModActive,
                instructions: autoModInstructions,
                scores: values,
              }}
              isLoading={isSavingAutomod}
              onClick={handleAutomodSave}
            />
          </div>

          <hr className="my-6 border-gray-700" />

          <div>
            <h3 className="font-semibold text-lg text-white mb-2">
              Test Moderation
            </h3>
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <input
                type="text"
                autoComplete="off"
                className="bg-black border border-gray-600 rounded p-2 flex-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet"
                placeholder="Type a comment to test moderation..."
                value={modComment}
                onChange={(e) => setModComment(e.target.value)}
                disabled={isTestingMod}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    selectedSocial &&
                    modComment &&
                    !isTestingMod
                  ) {
                    handleTestModComment();
                  }
                }}
              />
              <button
                className={`bg-yellow rounded-xl px-4 py-2 text-black font-semibold hover:brightness-90 active:brightness-75 flex items-center justify-center min-w-[100px] ${
                  isTestingMod || !modComment.trim()
                    ? 'opacity-60 cursor-not-allowed'
                    : ''
                }`}
                onClick={handleTestModComment}
                disabled={isTestingMod || !modComment.trim()}
              >
                {isTestingMod ? (
                  <svg
                    className="animate-spin h-5 w-5 text-black"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                ) : (
                  'Test'
                )}
              </button>
            </div>
            {modCommentResult && (
              <div
                ref={resultRef}
                className="mt-4 p-4 bg-black border border-gray-700 rounded text-sm"
              >
                <div className="mb-2">
                  <strong>Result:</strong>{' '}
                  <span
                    className={`font-bold ${
                      modCommentResult.evaluation
                        ? 'text-red-400'
                        : 'text-green-400'
                    }`}
                  >
                    {modCommentResult.evaluation ? 'Moderated' : 'Allowed'}
                  </span>
                </div>
                <div>
                  <strong>Reason:</strong> {modCommentResult.reasoning}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* --- End Redesigned AutoMod Module --- */}
      </div>
    </div>
  );
};

const Save = ({
  initialValue,
  currentValue,
  onClick,
  isLoading = false, // Added isLoading prop with a default value
}: {
  initialValue: any;
  currentValue: any;
  onClick: () => Promise<void> | void; // onClick can be async
  isLoading?: boolean;
}) => {
  const isObject = (value: any) => typeof value === 'object' && value !== null;

  let hasChanges;
  if (isObject(initialValue) && isObject(currentValue)) {
    hasChanges = JSON.stringify(initialValue) !== JSON.stringify(currentValue);
  } else {
    hasChanges = initialValue !== currentValue;
  }

  const isButtonActive = hasChanges && !isLoading;
  const isButtonDisabled = !hasChanges || isLoading;

  return (
    <button
      className={`flex flex-row justify-center items-center mt-2 rounded-2xl p-2 w-24 self-end transition-colors duration-150
      ${
        isButtonActive
          ? 'bg-yellow text-black hover:brightness-90 active:brightness-75'
          : 'bg-white bg-opacity-10 text-white cursor-not-allowed'
      }`}
      onClick={onClick}
      disabled={isButtonDisabled}
    >
      {isLoading ? (
        <svg // Spinner will inherit text-white from the button's disabled state style
          className="animate-spin h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      ) : (
        <>
          Save
          <CheckIcon className="w-4 h-4 ml-2" />
        </>
      )}
    </button>
  );
};

const SettingsSection = ({
  id,
  title,
  description,
  isExpanded,
  onToggle,
  children,
  preview,
}: {
  id: string;
  title: string;
  description: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  preview?: React.ReactNode;
}) => {
  const isPreviewString = typeof preview === 'string';

  return (
    <div className="settings-container bio">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center w-full">
          <div className="min-w-[100px] max-w-[540px]">
            <h2 className="font-semibold text-xl truncate">{title}</h2>
          </div>
          {!isExpanded && preview && (
            <div
              className={`text-sm ${
                isPreviewString
                  ? 'ml-4 flex-1 text-gray-400 italic truncate text-right'
                  : 'ml-auto'
              }`}
              style={
                isPreviewString
                  ? {
                      background: 'none',
                      maskImage:
                        'linear-gradient(to left, transparent 0%, black 40px, black 100%)',
                      WebkitMaskImage:
                        'linear-gradient(to left, transparent 0%, black 40px, black 100%)',
                    }
                  : {}
              }
            >
              {preview}
            </div>
          )}
        </div>
        {isExpanded ? (
          <ChevronDownIcon className="w-5 h-5 text-white ml-2" />
        ) : (
          <ChevronRightIcon className="w-5 h-5 text-white ml-2" />
        )}
      </div>
      {isExpanded && (
        <>
          <p className="text-sm text-gray-300 mt-2 mb-3">{description}</p>
          {children}
        </>
      )}
    </div>
  );
};

export default SocialSettings;
